import classNames from "classnames";
import { useQuery } from "@apollo/client";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { type Path } from "@/router";
import useCapabilities, { useCanViewCareerServicesDashboardBool } from "@/hooks/useCapabilities";
import {
  ASSOCIATE_DASHBOARD_CELEBRATIONS_QUERY,
  ASSOCIATE_DASHBOARD_PACING_QUERY,
  ASSOCIATE_DASHBOARD_SIGNALS_QUERY,
  type AssociateDashboardCelebrationsQueryVariables,
  type AssociateDashboardPacingQueryVariables,
  type AssociateDashboardSignalsQueryVariables
} from "../queries";
import {
  toDashboardAssociateCelebrationGroups,
  toDashboardAssociatePacingRows,
  toDashboardAssociateSignalGroups
} from "../_utils/associateDashboardDisplay";
import { isInitialQueryLoading } from "../_utils/isInitialQueryLoading";
import DashboardPacingPanel from "./DashboardPacingPanel";
import DashboardSignalTable from "./DashboardSignalTable";
import CareerServicesDashboardGate from "./CareerServicesDashboardGate";
import DashboardTimeframeToggle from "./DashboardTimeframeToggle";
import { DASHBOARD_BOTTOM_SCROLL_PADDING_CLASS } from "../_utils/dashboardPanelStyles";
import { DEFAULT_DASHBOARD_WINDOW } from "../_utils/dashboardTimeframe";

const ASSOCIATE_DASHBOARD_PREVIEW_LIMIT = 3;

function AssociatesDashboardContent() {
  const [searchParams] = useSearchParams();
  const [dashboardWindow, setDashboardWindow] = useState(DEFAULT_DASHBOARD_WINDOW);
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canViewCareerServicesDashboard = useCanViewCareerServicesDashboardBool();
  const sectionId = searchParams.get("sectionId");
  const signalVariables: AssociateDashboardSignalsQueryVariables = {
    input: {
      window: dashboardWindow,
      previewLimit: ASSOCIATE_DASHBOARD_PREVIEW_LIMIT,
      ...(sectionId ? { sectionId } : {})
    }
  };
  const celebrationVariables: AssociateDashboardCelebrationsQueryVariables = {
    input: {
      window: dashboardWindow,
      previewLimit: ASSOCIATE_DASHBOARD_PREVIEW_LIMIT,
      ...(sectionId ? { sectionId } : {})
    }
  };
  const pacingVariables: AssociateDashboardPacingQueryVariables = {
    input: {
      ...(sectionId ? { sectionId } : {})
    }
  };
  const { data, previousData, loading, networkStatus, error } = useQuery(ASSOCIATE_DASHBOARD_SIGNALS_QUERY, {
    variables: signalVariables,
    skip: !capabilitiesLoaded || !canViewCareerServicesDashboard,
    fetchPolicy: "cache-and-network"
  });
  const {
    data: celebrationData,
    previousData: previousCelebrationData,
    loading: celebrationLoading,
    networkStatus: celebrationNetworkStatus,
    error: celebrationError
  } = useQuery(ASSOCIATE_DASHBOARD_CELEBRATIONS_QUERY, {
    variables: celebrationVariables,
    skip: !capabilitiesLoaded || !canViewCareerServicesDashboard,
    fetchPolicy: "cache-and-network"
  });
  const {
    data: pacingData,
    previousData: previousPacingData,
    loading: pacingLoading,
    networkStatus: pacingNetworkStatus,
    error: pacingError
  } = useQuery(ASSOCIATE_DASHBOARD_PACING_QUERY, {
    variables: pacingVariables,
    skip: !capabilitiesLoaded || !canViewCareerServicesDashboard,
    fetchPolicy: "cache-and-network"
  });
  const dashboardSignals = data?.associateDashboardSignals ?? previousData?.associateDashboardSignals;
  const dashboardCelebrations =
    celebrationData?.associateDashboardCelebrations ?? previousCelebrationData?.associateDashboardCelebrations;
  const dashboardPacing = pacingData?.associateDashboardPacing ?? previousPacingData?.associateDashboardPacing;
  const associateSignalGroups = toDashboardAssociateSignalGroups(dashboardSignals?.signals ?? []);
  const associateCelebrationGroups = toDashboardAssociateCelebrationGroups(dashboardCelebrations?.celebrations ?? []);
  const associatePacingRows = toDashboardAssociatePacingRows(dashboardPacing?.levels ?? []);
  const totalAssociateRows = associateSignalGroups.reduce((total, group) => total + group.rows.length, 0);
  const totalAssociateCelebrationRows = associateCelebrationGroups.reduce(
    (total, group) => total + group.rows.length,
    0
  );
  const dashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard &&
      isInitialQueryLoading({
        loading,
        networkStatus,
        itemCount: totalAssociateRows,
        hasData: dashboardSignals !== undefined,
        error
      }));
  const celebrationDashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard &&
      isInitialQueryLoading({
        loading: celebrationLoading,
        networkStatus: celebrationNetworkStatus,
        itemCount: totalAssociateCelebrationRows,
        hasData: dashboardCelebrations !== undefined,
        error: celebrationError
      }));
  const pacingDashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard &&
      isInitialQueryLoading({
        loading: pacingLoading,
        networkStatus: pacingNetworkStatus,
        itemCount: associatePacingRows.length,
        hasData: dashboardPacing !== undefined,
        error: pacingError
      }));
  const dashboardUnavailable = capabilitiesLoaded && !canViewCareerServicesDashboard;
  const dashboardError = Boolean(error && !dashboardSignals);
  const celebrationDashboardError = Boolean(celebrationError && !dashboardCelebrations);
  const pacingDashboardError = Boolean(pacingError && !dashboardPacing);

  return (
    <div
      className={classNames("mt-12 flex flex-col gap-6", DASHBOARD_BOTTOM_SCROLL_PADDING_CLASS)}
      data-testid="home-associates-dashboard"
    >
      <div className="flex w-full justify-start" data-testid="home-associates-timeframe-row">
        <DashboardTimeframeToggle
          value={dashboardWindow}
          onChange={setDashboardWindow}
          testId="home-associates-timeframe"
        />
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          <DashboardSignalTable
            title="Needs Attention"
            icon="warning"
            testId="home-associates-needs-attention"
            tableTestId="home-associates-needs-attention-table"
            tableAriaLabel="Associate needs attention"
            groupTestIdPrefix="home-associates-needs-attention-group"
            rowTestIdPrefix="home-associate-needs-attention-row"
            personLabel="associate"
            groups={associateSignalGroups}
            loading={dashboardLoading}
            error={dashboardError}
            unavailable={dashboardUnavailable}
            viewAll={{
              label: "View all associates",
              to: "/people?programTypes%5B%5D=ASSOCIATE" as Path,
              testId: "home-associates-view-all"
            }}
          />
          <DashboardSignalTable
            title="Celebrations"
            icon="celebration"
            testId="home-associates-celebrations"
            tableTestId="home-associates-celebrations-table"
            tableAriaLabel="Associate celebrations"
            groupTestIdPrefix="home-associates-celebrations-group"
            rowTestIdPrefix="home-associate-celebration-row"
            personLabel="associate"
            groups={associateCelebrationGroups}
            loading={celebrationDashboardLoading}
            error={celebrationDashboardError}
            unavailable={dashboardUnavailable}
          />
        </div>

        <aside className="flex w-full flex-col gap-4" data-testid="home-associates-pacing">
          <DashboardPacingPanel
            title="Associate Pacing"
            progress={associatePacingRows}
            testId="home-associates-progress"
            loading={pacingDashboardLoading}
            error={pacingDashboardError}
            unavailable={dashboardUnavailable}
          />
        </aside>
      </div>
    </div>
  );
}

export default function AssociatesDashboard() {
  return (
    <CareerServicesDashboardGate>
      <AssociatesDashboardContent />
    </CareerServicesDashboardGate>
  );
}
